import React, { useState, useEffect } from 'react';
import { getHospitals, getEmployeeTypeDisplayName } from '../services/api.ts';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import { compareShiftRowsByTypeThenEnd } from './boardViewHelpers.ts';

interface Props {
  selectedDate: string;
  targetTimeUtc: string;
  allocations: { operatingRoomScheduleId: string; allocatedNurseIds?: string[]; allocatedDoctorIds?: string[] }[];
  schedules: { scheduleId: string; startTimeUtc: string; endTimeUtc: string }[];
  employees: { employeeId: string; firstName: string; lastName: string; employeeType: string; homeHospitalId?: string; credentials?: string[]; isFloatable?: boolean; maxConcurrentNurseCoverageRatio?: number; maxConcurrentStudentCoverageRatio?: number }[];
  // Per-day shifts so we can surface start/end and order via the employee popover (parity with ShiftsCard).
  shifts?: { employeeId: string; startTimeUtc: string; endTimeUtc: string; order?: number }[];
}

const AllocatedShiftsCard: React.FC<Props> = ({ selectedDate, targetTimeUtc, allocations = [], schedules = [], employees = [], shifts = [] }) => {
  const [hospitals, setHospitals] = useState<any[]>([]);

  useEffect(() => {
    const loadHospitals = async () => {
      try {
        const response = await getHospitals();
        setHospitals(response.hospitals || []);
      } catch (error) {
        console.error('Error loading hospitals:', error);
      }
    };
    loadHospitals();
  }, []);

  const scheduleMap = new Map(schedules.map(s => [s.scheduleId, s]));
  const employeeMap = new Map(employees.map(e => [e.employeeId, e]));
  const t = new Date(targetTimeUtc).getTime();

  const getHospitalName = (hospitalId: string) => {
    const hospital = hospitals.find(h => h.hospitalId === hospitalId);
    return hospital ? hospital.name : undefined;
  };

  type Row = { employeeId: string; name: string; type: string; concurrent: number; endTimeUtc: string };
  const rows: Row[] = [];

  const doctorToOverlaps: Record<string, number> = {};

  // Far-future sentinel so employees missing a shift entry sort to the end of
  // their type group rather than getting parsed as Invalid Date.
  const MISSING_END = '9999-12-31T23:59:59.999Z';
  const shiftEndFor = (employeeId: string): string => {
    const shift = shifts.find(s => s.employeeId === employeeId);
    return shift?.endTimeUtc ?? MISSING_END;
  };

  allocations.forEach(a => {
    const sched = scheduleMap.get(a.operatingRoomScheduleId);
    if (!sched) return;
    const s = new Date(sched.startTimeUtc).getTime();
    const e = new Date(sched.endTimeUtc).getTime();
    const overlaps = t >= s && t < e;
    if (!overlaps) return;

    const nurseIds = a.allocatedNurseIds || [];
    const doctorIds = a.allocatedDoctorIds || [];

    nurseIds.forEach(id => {
      const emp = employeeMap.get(id);
      if (!emp) return;
      rows.push({ employeeId: id, name: `${emp.firstName} ${emp.lastName}`, type: emp.employeeType, concurrent: 1, endTimeUtc: shiftEndFor(id) });
    });

    doctorIds.forEach(id => {
      doctorToOverlaps[id] = (doctorToOverlaps[id] || 0) + 1;
    });
  });

  Object.keys(doctorToOverlaps).forEach(id => {
    const emp = employeeMap.get(id);
    if (!emp) return;
    rows.push({ employeeId: id, name: `${emp.firstName} ${emp.lastName}`, type: emp.employeeType, concurrent: doctorToOverlaps[id], endTimeUtc: shiftEndFor(id) });
  });

  rows.sort((a, b) => compareShiftRowsByTypeThenEnd(
    { employeeType: a.type, endTimeUtc: a.endTimeUtc },
    { employeeType: b.type, endTimeUtc: b.endTimeUtc }
  ));

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>Allocated Shifts</h3>
        <span style={{ fontSize: '0.9em', color: 'var(--text-muted)' }}>{rows.length} at {new Date(targetTimeUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      {rows.length === 0 ? (
        <p style={{ margin: '10px 0', fontStyle: 'italic', color: 'var(--text-muted)' }}>No allocations at this time</p>
      ) : (
        <div className="allocated-shifts-table" style={{ maxHeight: '260px', overflowY: 'auto' }}>
          <table style={{ fontSize: '0.85em', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 4px' }}>Employee</th>
                <th style={{ padding: '8px 4px' }}>Type</th>
                <th style={{ padding: '8px 4px', textAlign: 'center' }}>Concurrent allocations</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const employee = employeeMap.get(r.employeeId);
                const employeeShift = shifts.find(s => s.employeeId === r.employeeId);
                return (
                  <tr key={`${r.employeeId}-${idx}`}>
                    <td style={{ padding: '6px 4px', fontWeight: 500 }}>
                      {employee ? (
                        <EmployeeDetailsPopover
                          employee={employee}
                          hospitalName={employee.homeHospitalId ? getHospitalName(employee.homeHospitalId) : undefined}
                          shift={employeeShift ? {
                            startTimeUtc: employeeShift.startTimeUtc,
                            endTimeUtc: employeeShift.endTimeUtc,
                            order: employeeShift.order
                          } : undefined}
                        >
                          {r.name}
                        </EmployeeDetailsPopover>
                      ) : (
                        r.name
                      )}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      <span style={{
                        fontSize: '0.8em',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        backgroundColor: r.type === 'doctor' ? 'rgba(74, 159, 212, 0.16)' :
                                         r.type === 'nurse' ? 'rgba(217, 119, 6, 0.16)' : 'rgba(22, 163, 74, 0.16)',
                        color: r.type === 'doctor' ? 'var(--or-blue-mid)' :
                               r.type === 'nurse' ? 'var(--amber)' : 'var(--success-text)'
                      }}>
                        {getEmployeeTypeDisplayName(r.type)}
                      </span>
                    </td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>{r.type === 'doctor' ? r.concurrent : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AllocatedShiftsCard;
